import { useItemAlerts, useCreateAlert, useResolveAlert } from '../../hooks/useMaintenance';
import { ALERT_TYPES, ALERT_SEVERITIES } from '@siliconvault/shared';
import { useState } from 'react';
import { Button } from '../ui/Button';
import { Input, Select } from '../ui/Input';
import { useToast } from '../ui/Toast';
import { useTranslation } from '../../i18n';

const SEVERITY_COLORS: Record<string, string> = {
  critical: '#FF3333',
  high: '#FF6600',
  medium: '#FFB800',
  low: '#33FF33',
};

interface AlertListProps {
  itemId: string;
  canEdit: boolean;
}

export function AlertList({ itemId, canEdit }: AlertListProps) {
  const { data, isLoading } = useItemAlerts(itemId);
  const createMutation = useCreateAlert();
  const resolveMutation = useResolveAlert();
  const { toast } = useToast();
  const { t } = useTranslation();
  const [showCreate, setShowCreate] = useState(false);
  const [newAlert, setNewAlert] = useState({ alertType: 'custom' as string, severity: 'medium' as string, title: '', description: '' });

  const alerts = data?.data ?? [];

  async function handleCreate() {
    if (!newAlert.title.trim()) return;
    try {
      await createMutation.mutateAsync({ itemId, data: newAlert });
      toast('Alert created', 'success');
      setShowCreate(false);
      setNewAlert({ alertType: 'custom', severity: 'medium', title: '', description: '' });
    } catch {
      toast('Failed to create alert', 'error');
    }
  }

  async function handleResolve(alertId: string, resolved: boolean) {
    try {
      await resolveMutation.mutateAsync({ alertId, resolved });
      toast(resolved ? 'Alert resolved' : 'Alert reopened', 'success');
    } catch {
      toast('Failed to update alert', 'error');
    }
  }

  return (
    <div style={{ marginTop: '1.5rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
        <h3 style={{ fontSize: '0.8125rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-secondary)' }}>
          {t('alerts.title')}
        </h3>
        {canEdit && (
          <Button size="small" variant="secondary" onClick={() => setShowCreate(!showCreate)}>
            {showCreate ? 'Cancel' : '+ Add Alert'}
          </Button>
        )}
      </div>

      {showCreate && (
        <div style={{
          padding: '0.75rem',
          marginBottom: '0.75rem',
          background: 'var(--bg-secondary)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius)',
          display: 'flex',
          flexDirection: 'column',
          gap: '0.5rem',
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
            <Select label={t('alerts.type')} value={newAlert.alertType} onChange={(e) => setNewAlert({ ...newAlert, alertType: e.target.value })}>
              <option value="capacitor_check">{t('alerts.capacitorCheck')}</option>
              <option value="battery_check">{t('alerts.batteryCheck')}</option>
              <option value="cleaning">{t('alerts.cleaning')}</option>
              <option value="testing">{t('alerts.testing')}</option>
              <option value="repair">{t('alerts.repair')}</option>
              <option value="other">{t('alerts.other')}</option>
            </Select>
            <Select label={t('alerts.severity')} value={newAlert.severity} onChange={(e) => setNewAlert({ ...newAlert, severity: e.target.value })}>
              {ALERT_SEVERITIES.map((s) => <option key={s} value={s}>{s}</option>)}
            </Select>
          </div>
          <Input label={t('alerts.alertTitle')} value={newAlert.title} onChange={(e) => setNewAlert({ ...newAlert, title: e.target.value })} required />
          <Input label={t('alerts.description')} value={newAlert.description} onChange={(e) => setNewAlert({ ...newAlert, description: e.target.value })} placeholder={t('alerts.optionalDetails')} />
          <Button size="small" onClick={handleCreate}>{t('alerts.createAlert')}</Button>
        </div>
      )}

      {isLoading ? (
        <div style={{ color: 'var(--text-muted)', fontSize: '0.8125rem' }}>{t('alerts.loading')}</div>
      ) : alerts.length === 0 ? (
        <div style={{
          padding: '1.25rem',
          background: 'var(--bg-secondary)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius)',
          color: 'var(--text-muted)',
          fontSize: '0.8125rem',
          fontFamily: 'var(--font-mono)',
          textAlign: 'center',
        }}>
          {t('alerts.noActive')}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
          {alerts.map((alert: any) => (
            <div key={alert.id} style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              padding: '0.625rem 0.875rem',
              background: 'var(--bg-secondary)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius)',
              opacity: alert.isResolved ? 0.5 : 1,
            }}>
              <span style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: alert.isResolved ? '#555' : (SEVERITY_COLORS[alert.severity] ?? '#888'),
                flexShrink: 0,
              }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '0.8125rem', fontWeight: 500 }}>{alert.title}</div>
                {alert.description && (
                  <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', marginTop: '0.125rem' }}>{alert.description}</div>
                )}
              </div>
              <span style={{
                fontSize: '0.5625rem',
                fontFamily: 'var(--font-mono)',
                textTransform: 'uppercase',
                color: SEVERITY_COLORS[alert.severity] ?? '#888',
              }}>
                {alert.severity}
              </span>
              {canEdit && (
                <Button
                  size="small"
                  variant="secondary"
                  onClick={() => handleResolve(alert.id, !alert.isResolved)}
                >
                  {alert.isResolved ? t('alerts.reopen') : t('alerts.resolve')}
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
